import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import { INotification } from 'app/models/notifications/INotification';
import { IPresenceNotification } from 'app/models/notifications/IPresenceNotification';
import { NotificationName } from 'app/types/notifications';
import { rootConfig } from 'config';
import useAuth from 'hooks/useAuth';
import { createContext, useContext, useEffect, useState } from 'react';

interface ContextValueTypes {
  onlineUsers: IPresenceNotification;
}

const initialState: ContextValueTypes = {
  onlineUsers: { onlineCount: 0, userIds: [] },
};

const NotificationsContext = createContext<ContextValueTypes>({
  onlineUsers: initialState.onlineUsers,
});

interface Props {
  children: React.ReactNode;
}

export const NotificationsProvider = ({ children }: Props) => {
  const [onlineUsers, setOnlineUsers] = useState<IPresenceNotification>(initialState.onlineUsers);

  const { accessToken, user } = useAuth();
  const [connection, setConnection] = useState<HubConnection | null>(null);
  useEffect(() => {
    if (!accessToken || !user?.id) return;
    const connect = new HubConnectionBuilder()
      .withUrl(`${rootConfig.serverUrl}/notifications`, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => accessToken,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();
    setConnection(connect);
    connect
      .start()
      .then(() => {
        connect.on(
          'NotificationFromServer',
          (notificationName: NotificationName, notification: INotification) => {
            if (notificationName === 'Apollo.Domain.Notifications.PresenceNotification') {
              setOnlineUsers(notification as IPresenceNotification);
            }
          },
        );
        connect.invoke('HandleUserConnected', user.id);
      })

      .catch((err) => console.error('Error while connecting to SignalR Hub:', err));

    return () => {
      if (connection) {
        connection.off('NotificationFromServer');
      }
    };
  }, [accessToken, user?.id]);

  return (
    <NotificationsContext.Provider
      value={{
        onlineUsers,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const NotificationsConsumer = NotificationsContext.Consumer;

export const useNotifications = () => {
  const context = useContext(NotificationsContext);

  return context;
};

export default NotificationsContext;
